/**
 * useNotificationSSE Hook
 *
 * Establishes Server-Sent Events connection for real-time notifications.
 * Automatically reconnects on disconnect with exponential backoff.
 *
 * Usage:
 * ```tsx
 * // In App.tsx or a top-level provider
 * function NotificationProvider({ children }) {
 *   useNotificationSSE();
 *   return <>{children}</>;
 * }
 * ```
 */

import { useEffect, useRef, useCallback } from 'react';
import { useNotificationCenterStore } from '@/stores/notificationCenterStore';
import { notify } from '@/stores/notificationStore';
import { getAccessToken } from '@/services/tokenManager';
import { logger } from '@/utils/logger';
import { API_URL } from '@/utils/api';
import type { Notification } from '../types/notification.types';

// ============================================================================
// Types
// ============================================================================

interface SSEMessage {
  event: string;
  data: unknown;
}

interface NotificationNewEvent {
  id: string;
  tenantId: string;
  userId: string;
  type: string;
  category: string;
  priority: string;
  title: string;
  message: string;
  link?: string;
  isRead: boolean;
  createdAt: string;
  [key: string]: unknown;
}

interface NotificationReadEvent {
  id: string;
}

interface NotificationDismissedEvent {
  id: string;
}

interface UnreadCountEvent {
  unreadCount: number;
  hasUrgent: boolean;
}

interface SSEConfig {
  /** Base URL for SSE endpoint (default: /notifications/sse/stream) */
  endpoint?: string;
  /** Reconnect delay in ms (default: 1000) */
  reconnectDelay?: number;
  /** Max reconnect delay in ms (default: 30000) */
  maxReconnectDelay?: number;
  /** Enable debug logging */
  debug?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_ENDPOINT = '/notifications/sse/stream';
const DEFAULT_RECONNECT_DELAY = 1000;
const MAX_RECONNECT_DELAY = 30000;

// ============================================================================
// Hook
// ============================================================================

export function useNotificationSSE(config: SSEConfig = {}) {
  const {
    endpoint = DEFAULT_ENDPOINT,
    reconnectDelay = DEFAULT_RECONNECT_DELAY,
    maxReconnectDelay = MAX_RECONNECT_DELAY,
    debug = false,
  } = config;

  // Store actions
  const addNotification = useNotificationCenterStore((state) => state.addNotification);
  const updateNotification = useNotificationCenterStore((state) => state.updateNotification);
  const removeNotification = useNotificationCenterStore((state) => state.removeNotification);
  const setUnreadCount = useNotificationCenterStore((state) => state.setUnreadCount);

  // Refs
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isConnectedRef = useRef(false);

  // Logging helper
  const log = useCallback(
    (message: string, data?: unknown) => {
      if (debug) {
        logger.debug(`[SSE] ${message}`, data || '');
      }
    },
    [debug]
  );

  // Calculate reconnect delay with exponential backoff
  const getReconnectDelay = useCallback(() => {
    const delay = Math.min(
      reconnectDelay * Math.pow(2, reconnectAttemptRef.current),
      maxReconnectDelay
    );
    return delay;
  }, [reconnectDelay, maxReconnectDelay]);

  // Handle incoming SSE events
  const handleMessage = useCallback(
    (event: MessageEvent, eventType: string) => {
      try {
        const data = JSON.parse(event.data);
        log(`Event received: ${eventType}`, data);

        switch (eventType) {
          case 'notification:new':
            handleNewNotification(data as NotificationNewEvent);
            break;

          case 'notification:read':
            handleNotificationRead(data as NotificationReadEvent);
            break;

          case 'notification:dismissed':
            handleNotificationDismissed(data as NotificationDismissedEvent);
            break;

          case 'notification:count_update':
            handleCountUpdate(data as UnreadCountEvent);
            break;

          case 'connected':
            log('Connection established', data);
            isConnectedRef.current = true;
            reconnectAttemptRef.current = 0;
            break;

          case 'heartbeat':
            // Just a keep-alive, no action needed
            break;

          default:
            log(`Unknown event type: ${eventType}`, data);
        }
      } catch (error) {
        logger.error('[SSE] Failed to parse message:', error);
      }
    },
    [log]
  );

  // Event handlers
  const handleNewNotification = useCallback(
    (data: NotificationNewEvent) => {
      const notification: Notification = {
        id: data.id,
        tenantId: data.tenantId || '',
        userId: data.userId || '',
        type: data.type as Notification['type'],
        category: data.category as Notification['category'],
        priority: data.priority as Notification['priority'],
        title: data.title,
        message: data.message,
        link: data.link,
        isRead: data.isRead ?? false,
        isDismissed: false,
        isArchived: false,
        channels: ['IN_APP'] as Notification['channels'],
        createdAt: data.createdAt,
        updatedAt: data.createdAt,
        actionButtons: data.actionButtons as Notification['actionButtons'],
        aiGenerated: Boolean(data.aiGenerated),
        groupCount: 1,
      };

      // Add to notification center
      addNotification(notification);

      // Auto-display toast for important notifications
      // Priority HIGH/URGENT always show toast
      // Categories BILLING, PAYMENTS, FISCAL show toast for NORMAL priority too
      const shouldShowToast =
        notification.priority === 'URGENT' ||
        notification.priority === 'HIGH' ||
        (notification.priority === 'NORMAL' &&
          ['BILLING', 'PAYMENTS', 'FISCAL', 'TREASURY'].includes(notification.category || ''));

      if (shouldShowToast) {
        // Map priority to toast type
        const toastType = notification.priority === 'URGENT' ? 'error' :
                          notification.priority === 'HIGH' ? 'warning' : 'success';

        // Show toast with appropriate styling
        if (toastType === 'error') {
          notify.error(notification.message, notification.title, 8000);
        } else if (toastType === 'warning') {
          notify.warning(notification.message, notification.title, 6000);
        } else {
          notify.success(notification.message, notification.title, 5000);
        }

        log(`Toast displayed for notification: ${notification.id} (${notification.priority})`);
      }

      // Play notification sound for urgent notifications
      if (notification.priority === 'URGENT') {
        // playNotificationSound();
      }
    },
    [addNotification, log]
  );

  const handleNotificationRead = useCallback(
    (data: NotificationReadEvent) => {
      updateNotification(data.id, {
        isRead: true,
        readAt: new Date().toISOString(),
      });
    },
    [updateNotification]
  );

  const handleNotificationDismissed = useCallback(
    (data: NotificationDismissedEvent) => {
      removeNotification(data.id);
    },
    [removeNotification]
  );

  const handleCountUpdate = useCallback(
    (data: UnreadCountEvent) => {
      setUnreadCount(data.unreadCount);
      // hasUrgent is handled separately via store logic
    },
    [setUnreadCount]
  );

  // Connect to SSE
  const connect = useCallback(() => {
    // Get auth token from TokenManager
    const token = getAccessToken();
    if (!token) {
      log('No auth token, skipping SSE connection');
      return;
    }

    // Clean up existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    // Build URL with auth token (SSE doesn't support headers natively)
    // We'll use a query param that the backend extracts
    const url = `${API_URL}${endpoint}?token=${encodeURIComponent(token)}`;

    log('Connecting to SSE...', { endpoint });

    try {
      // Note: withCredentials n'est pas nécessaire car l'auth passe par le token query param
      // et CORS avec withCredentials:true nécessite un origin spécifique (pas *)
      const eventSource = new EventSource(url);

      // Generic message handler
      eventSource.onmessage = (event) => {
        handleMessage(event, 'message');
      };

      // Specific event handlers
      eventSource.addEventListener('connected', (event) => {
        handleMessage(event as MessageEvent, 'connected');
      });

      eventSource.addEventListener('notification:new', (event) => {
        handleMessage(event as MessageEvent, 'notification:new');
      });

      eventSource.addEventListener('notification:read', (event) => {
        handleMessage(event as MessageEvent, 'notification:read');
      });

      eventSource.addEventListener('notification:dismissed', (event) => {
        handleMessage(event as MessageEvent, 'notification:dismissed');
      });

      eventSource.addEventListener('notification:count_update', (event) => {
        handleMessage(event as MessageEvent, 'notification:count_update');
      });

      eventSource.addEventListener('heartbeat', (event) => {
        handleMessage(event as MessageEvent, 'heartbeat');
      });

      // Connection opened
      eventSource.onopen = () => {
        log('Connection opened');
      };

      // Connection error
      eventSource.onerror = (error) => {
        log('Connection error', error);
        isConnectedRef.current = false;

        // Close and attempt reconnect
        eventSource.close();
        scheduleReconnect();
      };

      eventSourceRef.current = eventSource;
    } catch (error) {
      logger.error('[SSE] Failed to create EventSource:', error);
      scheduleReconnect();
    }
  }, [endpoint, handleMessage, log]);

  // Schedule reconnection
  const scheduleReconnect = useCallback(() => {
    // Clear any existing timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    reconnectAttemptRef.current += 1;
    const delay = getReconnectDelay();

    log(`Scheduling reconnect in ${delay}ms (attempt ${reconnectAttemptRef.current})`);

    reconnectTimeoutRef.current = setTimeout(() => {
      connect();
    }, delay);
  }, [connect, getReconnectDelay, log]);

  // Disconnect
  const disconnect = useCallback(() => {
    log('Disconnecting...');

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    isConnectedRef.current = false;
    reconnectAttemptRef.current = 0;
  }, [log]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect();

    // Handle visibility change (reconnect when tab becomes visible)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isConnectedRef.current) {
        log('Tab visible, reconnecting...');
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      disconnect();
    };
  }, [connect, disconnect, log]);

  // Reconnect when token changes
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'accessToken') {
        if (event.newValue) {
          log('Token changed, reconnecting...');
          connect();
        } else {
          log('Token removed, disconnecting...');
          disconnect();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [connect, disconnect, log]);

  return {
    isConnected: isConnectedRef.current,
    reconnect: connect,
    disconnect,
  };
}

export default useNotificationSSE;
